import { defineStore } from "pinia";
import { type ListItem } from "@airpr/360-ui-components";
import { useSearchStore } from "~/store/search";
import { useOrgStore } from "~/store/org";
import ChartState, {
  MentionChartData,
  ConvertedArticleSentimentChartData,
  HighchartDataFormt,
  ChartTemplate,
  AnalyticsChart,
} from "~/types/ChartsType";
import {
  ArticleSentimentChartData,
  ArticleChartData,
  CountByMediaType,
} from "~/types/attributes/ChartDataAttributesType";
import AnalyticsChartAttributes from "~/types/attributes/AnalyticsChartAttributes";
import RequestOptions from "~/types/RequestOptionsType";
import { useApiCall } from "~/composables/useApiCall";
import { useRequestQuery } from "~/composables/useRequestQuery";

export const useChartsStore = defineStore("charts", {
  state: (): ChartState => ({
    isLoading: false,
    aveOverTimeData: [],
    dashboardChartsData: [], // all charts data from each profile
    shareOfVoiceData: [],
    shareOfVoiceOverTimeData: [],
    shareOfVoiceTotal: 0,
    comparisonCoverageData: [],
    totalSentiment: { negative: 0, positive: 0, neutral: 0 },
    sentimentLineChartData: [],
    shareOfReachData: [],
    shareOfReachOverTimeData: [],
    shareOfAveData: [],
    shareOfAmplificationData: [],
    shareOfAmpOverTimeData: [],
    reachByMediaTypeData: {
      print: 0,
      radio: 0,
      tv: 0,
      web: 0,
    },
    articleCountByMediaTypeData: {
      print: 0,
      radio: 0,
      tv: 0,
      web: 0,
    },
    chartTemplates: [],
    analyticsCharts: new Map(),
  }),
  getters: {
    selectedChartTemplate: (state) => {
      const savedSearchStore = useSearchStore();
      let templateId;
      if (savedSearchStore.selectedSearch.analytics_chart_template_id) {
        templateId =
          savedSearchStore.selectedSearch.analytics_chart_template_id;
      } else if (savedSearchStore.selectedSearch.is_comparison) {
        templateId = "2";
      } else {
        templateId = "1";
      }

      return state.chartTemplates.find(
        (template: ChartTemplate) => Number(template.id) === Number(templateId),
      );
    },
    shareOfVoice: (state) => {
      return state.shareOfVoiceData.length > 0
        ? [
            {
              type: "pie",
              name: "Mentions",
              data: state.shareOfVoiceData,
            },
          ]
        : [{ type: "pie", name: "Mentions", data: [] }];
    },
    shareOfVoiceSum: (state) => {
      return state.shareOfVoiceTotal;
    },
    shareOfVoiceOverTime: (state) => {
      return state.shareOfVoiceOverTimeData.length > 0
        ? state.shareOfVoiceOverTimeData
        : null;
    },
    shareOfReach: (state) => {
      return state.shareOfReachData.length > 0
        ? [
            {
              type: "pie",
              name: "Reach",
              data: state.shareOfReachData,
            },
          ]
        : [{}];
    },
    shareOfReachOverTime: (state) => {
      return state.shareOfReachOverTimeData.length > 0
        ? state.shareOfReachOverTimeData
        : null;
    },
    shareOfAve: (state) => {
      return state.shareOfAveData.length > 0
        ? [
            {
              type: "pie",
              name: "AVE",
              data: state.shareOfAveData,
            },
          ]
        : [{}];
    },
    aveOverTime: (state) => {
      return state.aveOverTimeData.length > 0 ? state.aveOverTimeData : null;
    },
    shareOfAmplification: (state) => {
      return state.shareOfAmplificationData.length > 0
        ? [
            {
              type: "pie",
              name: "Amplification",
              data: state.shareOfAmplificationData,
            },
          ]
        : [{}];
    },
    shareOfAmpOverTime: (state) => {
      return state.shareOfAmpOverTimeData.length > 0
        ? state.shareOfAmpOverTimeData
        : null;
    },
    comparisonCoverage: (state) => {
      return state.comparisonCoverageData.length > 0
        ? state.comparisonCoverageData
        : null;
    },
    sentimentChartSeries: (state) => {
      return [
        {
          type: "pie",
          name: "Articles",
          data: [
            {
              name: "Positive",
              y: state.totalSentiment.positive,
              color: "#14AE49",
            },
            {
              name: "Neutral",
              y: state.totalSentiment.neutral,
              color: "#D4D4D8",
            },
            {
              name: "Negative",
              y: state.totalSentiment.negative,
              color: "#FB8989",
            },
          ],
        },
      ];
    },
    sentimentOverTime: (state) => {
      const positives: HighchartDataFormt = {
        data: [],
        name: "Positive",
        type: "line",
        color: "#14AE49",
      };
      const neutrals: HighchartDataFormt = {
        data: [],
        name: "Neutral",
        type: "line",
        color: "#D4D4D8",
      };
      const negatives: HighchartDataFormt = {
        data: [],
        name: "Negative",
        type: "line",
        color: "#FB8989",
      };
      if (state.sentimentLineChartData.length > 0) {
        // sort the sentiment by date first or chart breaks.
        state.sentimentLineChartData.sort(
          (
            a: ConvertedArticleSentimentChartData,
            b: ConvertedArticleSentimentChartData,
          ) => a.date - b.date,
        );
        state.sentimentLineChartData.forEach(
          (lineData: ConvertedArticleSentimentChartData) => {
            positives.data.push([lineData.date, lineData.positive]);
            neutrals.data.push([lineData.date, lineData.neutral]);
            negatives.data.push([lineData.date, lineData.negative]);
          },
        );
      }

      return [positives, neutrals, negatives];
    },
    reachByMediaType: (state) => {
      return [
        {
          type: "pie",
          name: "Mentions",
          data: [
            {
              name: "Web",
              y: state.reachByMediaTypeData.web,
            },
            {
              name: "Print",
              y: state.reachByMediaTypeData.print,
            },
            {
              name: "Tv",
              y: state.reachByMediaTypeData.tv,
            },
            {
              name: "Radio",
              y: state.reachByMediaTypeData.radio,
            },
          ],
        },
      ];
    },
    articleCountByMediaType: (state) => {
      return [
        {
          type: "pie",
          name: "Mentions",
          data: [
            {
              name: "Print",
              y: state.articleCountByMediaTypeData.print,
            },
            {
              name: "Radio",
              y: state.articleCountByMediaTypeData.radio,
            },
            {
              name: "Tv",
              y: state.articleCountByMediaTypeData.tv,
            },
            {
              name: "Web",
              y: state.articleCountByMediaTypeData.web,
            },
          ],
        },
      ];
    },
  },
  actions: {
    async fetchDashboardChartData() {
      try {
        this.isLoading = true;
        this.resetToDefault();
        const savedSearchStore = useSearchStore();
        savedSearchStore.updateQuerySearchType(1);
        for (const profile of savedSearchStore.nestedSearches) {
          const requestQuery = await useRequestQuery(
            "chart-searches",
            profile.attributes.profile_search_text,
            profile.id,
          );
          const options: RequestOptions = {
            method: "POST",
            body: requestQuery,
          };
          const resp = await useApiCall("/chart_searches", options);

          if (resp.data?._rawValue?.data) {
            // Filter the raw data to get a unique list based on the profile ID.
            const newItem = resp.data._rawValue.data;
            const newProfileId = newItem?.relationships?.profile?.data?.id;

            const alreadyExists = this.dashboardChartsData.some(
              (item) => item.relationships.profile.data.id === newProfileId,
            );

            if (!alreadyExists) {
              this.dashboardChartsData.push(newItem);
            }
          }
        }

        this.compileChartsData();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    resetToDefault() {
      this.aveOverTimeData = [];
      this.shareOfVoiceData = [];
      this.shareOfVoiceTotal = 0;
      this.shareOfVoiceOverTimeData = [];
      this.comparisonCoverageData = [];
      this.totalSentiment = { negative: 0, positive: 0, neutral: 0 };
      this.sentimentLineChartData = [];
      this.shareOfReachData = [];
      this.shareOfReachOverTimeData = [];
      this.shareOfAveData = [];
      this.shareOfAmplificationData = [];
      this.shareOfAmpOverTimeData = [];
      this.articleCountByMediaTypeData = {
        print: 0,
        radio: 0,
        tv: 0,
        web: 0,
      };
      this.reachByMediaTypeData = {
        print: 0,
        radio: 0,
        tv: 0,
        web: 0,
      };
    },

    compileChartsData() {
      const currentProfile = JSON.parse(
        localStorage.getItem("currentProfile") || "{}",
      );

      this.dashboardChartsData.forEach(
        (profileChartsData: MentionChartData) => {
          const savedSearchStore = useSearchStore();
          const profileId = profileChartsData.relationships.profile.data.id;
          const matchingProfile = savedSearchStore.nestedSearches.find(
            (search) => search.id === profileId,
          );
          const profileName = matchingProfile?.attributes.name || "";

          const currentProfileSentiments =
            profileChartsData?.attributes?.search_result?.counts
              .article_sentiment;
          const currentProfileReach =
            profileChartsData?.attributes?.search_result?.counts.reach;
          const currentProfileSocial =
            profileChartsData?.attributes?.search_result?.counts.social;
          const currentProfileArticles =
            profileChartsData?.attributes?.search_result?.counts?.articles;
          const currentMediaTypeData =
            profileChartsData?.attributes?.search_result?.counts_by_media_type;
          const currentMentionTotal =
            profileChartsData?.attributes?.search_result?.total || 0;
          const currentProfileAve =
            profileChartsData?.attributes?.search_result?.counts.ave;

          this.handleComparisonCoverage(profileName, currentProfileArticles);

          this.chartDataBuilder(profileName, currentProfileSocial, "Amp");
          this.chartDataBuilder(profileName, currentProfileAve, "Ave");
          this.chartDataBuilder(profileName, currentProfileReach, "Reach");
          this.chartDataBuilder(
            profileName,
            currentProfileArticles,
            "Voice",
            currentMentionTotal,
          );

          if (
            currentProfile?.data?.id ===
            profileChartsData?.relationships?.profile?.data?.id
          ) {
            // Include media type and sentiment data only for the current profile in the charts.
            this.handleMediaTypes(currentMediaTypeData);
            this.handleSentiments(currentProfileSentiments);
          }
        },
      );
    },

    handleMediaTypes(currentMediaTypeData: CountByMediaType) {
      for (const mediaTypeKey in currentMediaTypeData) {
        const mediaType = currentMediaTypeData[mediaTypeKey];
        const articleCount = mediaType.articles.reduce(
          (sum: number, curr: ArticleChartData) => {
            return sum + curr.total;
          },
          0,
        );
        const articleReach = mediaType.reach.reduce(
          (sum: number, curr: ArticleChartData) => {
            return sum + curr.total;
          },
          0,
        );
        this.reachByMediaTypeData[mediaTypeKey] += articleReach;
        this.articleCountByMediaTypeData[mediaTypeKey] += articleCount;
      }
    },

    chartDataBuilder(
      profileName: string,
      dataArray: Array<ArticleChartData>,
      type: string,
      mentionCount: number = 0,
    ) {
      let total = 0;
      const compiledData: Array<Array<number>> = [];
      dataArray?.forEach((item: ArticleChartData) => {
        compiledData.push([Date.parse(item.date), item.total]);
        total += item.total;
      });
      switch (type) {
        case "Amp":
          // handle Amplification
          this.shareOfAmpOverTimeData.push({
            data: compiledData,
            name: profileName,
            type: "column",
          });
          this.shareOfAmplificationData.push({
            name: profileName,
            y: total,
          });
          break;
        case "Ave":
          // handle AVE
          this.aveOverTimeData.push({
            data: compiledData,
            name: profileName,
            type: "column",
          });
          this.shareOfAveData.push({
            name: profileName,
            y: total,
          });
          break;
        case "Reach":
          // handle Reach
          this.shareOfReachOverTimeData.push({
            data: compiledData,
            name: profileName,
            type: "column",
          });
          this.shareOfReachData.push({
            name: profileName,
            y: total,
          });
          break;
        case "Voice":
          // handle Share Of Voice
          this.shareOfVoiceOverTimeData.push({
            data: compiledData,
            name: profileName,
            type: "column",
          });
          this.shareOfVoiceData.push({
            name: profileName,
            y: mentionCount,
          });
          this.shareOfVoiceTotal += mentionCount || 0;
          break;
      }
    },

    handleComparisonCoverage(
      profileName: string,
      articles: Array<ArticleChartData>,
    ) {
      const parsedArticles = articles.map(
        ({ total, date }: { total: number; date: string }) => [
          Date.parse(date),
          total,
        ],
      );
      this.comparisonCoverageData.push({
        type: "line",
        name: profileName,
        data: parsedArticles,
      });
    },

    handleSentiments(
      currentProfileSentiments: Array<ArticleSentimentChartData>,
    ) {
      currentProfileSentiments.forEach((sentiment) => {
        const matchingSentimentObject = this.sentimentLineChartData.find(
          (data: ArticleSentimentChartData) =>
            Date.parse(data.date) === Date.parse(sentiment.date),
        );
        if (matchingSentimentObject) {
          matchingSentimentObject.positive += sentiment.positive;
          matchingSentimentObject.negative += sentiment.negative;
          matchingSentimentObject.neutral += sentiment.neutral;
        } else {
          this.sentimentLineChartData.push({
            date: Date.parse(sentiment.date),
            positive: sentiment.positive,
            negative: sentiment.negative,
            neutral: sentiment.neutral,
          });
        }

        // Compiling Sentiment Totals
        this.totalSentiment.positive += sentiment.positive;
        this.totalSentiment.negative += sentiment.negative;
        this.totalSentiment.neutral += sentiment.neutral;
      });
    },
    async fetchOrgTemplates() {
      const options: RequestOptions = {
        method: "GET",
      };

      const resp = await useApiCall("/analytics_chart_templates", options);
      this.chartTemplates = resp.data._rawValue.data;
    },
    async saveTemplate(
      analyticsTemplate: ListItem,
      isUserDefaultTemplate: boolean = true,
      chartArray = null,
    ) {
      const orgStore = useOrgStore();
      const savedSearchStore = useSearchStore();

      // NOTE: A new record is created in the database in the following cases:
      // 1. A new element is selected from the dropdown (id is 0).
      // 2. The current selection uses a default template (id is 1 or 2).
      const isNew = Number(analyticsTemplate.id) <= 2;

      const charts =
        chartArray ||
        this.analyticsCharts.get(String(this.selectedChartTemplate.id));

      // Step 1: Save Analytics Chart Template
      const options: RequestOptions = {
        method: isNew ? "POST" : "PATCH",
        body: {
          data: {
            type: "analytics_chart_template",
            attributes: {
              name: isUserDefaultTemplate ? null : analyticsTemplate.name,
              organization_id: orgStore.org?.id,
              search_dashboard_id: isUserDefaultTemplate
                ? savedSearchStore.selectedSearch.id
                : null,
              is_comparison: savedSearchStore.selectedSearch.is_comparison,
            },
          },
          relationships: {
            charts,
          },
        },
      };

      const analyticsChartTemplatesPath = isNew
        ? "/analytics_chart_templates"
        : `/analytics_chart_templates/${analyticsTemplate.id}`;
      const resp = await useApiCall(analyticsChartTemplatesPath, options);

      if (resp.data?._rawValue?.data) {
        // Add or Update Analytics Chart Template in the store
        if (isNew) {
          this.chartTemplates.push(resp.data._rawValue.data);
        } else {
          const existingIndex = this.chartTemplates.findIndex(
            (template: ChartTemplate) =>
              template.id === resp.data?._rawValue?.data?.id,
          );
          if (existingIndex !== -1) {
            this.chartTemplates[existingIndex] = resp.data?._rawValue?.data;
          }
        }

        // Step 2: Update the Search Dashboard only if saving a new default
        if (isNew && isUserDefaultTemplate) {
          savedSearchStore.selectedSearch.analytics_chart_template_id =
            resp.data._rawValue.data.id;
          await savedSearchStore.patchSearch();
        }

        // Step 3: Re-Fetch Analytics Charts
        this.fetchChartsByTemplateID();

        return {
          template: resp.data._rawValue.data,
        };
      } else {
        throw new Error("charts.chartsAnalyticsTemplateErrorSaving");
      }
    },
    async applyTemplate(analyticsTemplate: ListItem) {
      try {
        const savedSearchStore = useSearchStore();
        const dashboardId = savedSearchStore.selectedSearch.id;
        const oldTemplateId =
          savedSearchStore.selectedSearch.analytics_chart_template_id;
        const requestOptions: RequestOptions = {
          method: "POST",
          body: {
            templateId: analyticsTemplate.id,
            dashboardId,
          },
        };

        const replaceIdx = this.chartTemplates.findIndex(
          (item: ChartTemplate) => String(item.id) === String(oldTemplateId),
        );

        const resp = await useApiCall(
          `analytics_chart_templates/apply_template`,
          requestOptions,
        );

        const templateData = resp.data?._rawValue;

        if (replaceIdx !== -1) {
          this.chartTemplates[replaceIdx] = templateData.data;
        } else {
          this.chartTemplates.push(templateData.data);
        }

        this.analyticsCharts.set(templateData.data.id, templateData.included);
        this.analyticsCharts.delete(oldTemplateId);

        savedSearchStore.selectedSearch.analytics_chart_template_id =
          templateData.data.id;
        // Ensure all cached searches have the right template associations
        await savedSearchStore.fetchSearches();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Caught an unexpected error:", error);
        throw new Error("charts.chartsAnalyticsTemplateErrorApplying");
      }
    },
    async saveAnalyticsChart(
      analyticsChart: AnalyticsChart | null,
      analyticsChartTemplateID: string | number,
      isNew: boolean,
      customAttributes: AnalyticsChartAttributes | null = null,
      editChartId: number | null = null,
    ) {
      let attributeOption = null;
      if (analyticsChart) {
        attributeOption = {
          title: analyticsChart.attributes.title,
          tooltip: analyticsChart.attributes.tooltip,
          metric: analyticsChart.attributes.metric,
          slice_by: analyticsChart.attributes.slice_by,
          chart_type: analyticsChart.attributes.chart_type,
          rows: analyticsChart.attributes.rows,
          columns: analyticsChart.attributes.columns,
          position: analyticsChart.attributes.position,
          comparison: analyticsChart.attributes.comparison,
          analytics_chart_template_id: analyticsChartTemplateID,
        };
      } else {
        attributeOption = customAttributes;
      }

      const options: RequestOptions = {
        method: isNew ? "POST" : "PATCH",
        body: {
          data: {
            type: "analytics_chart",
            attributes: attributeOption,
            relationships: {
              analytics_chart_template: {
                data: {
                  id: analyticsChartTemplateID,
                  type: "analytics_chart_template",
                },
              },
            },
          },
        },
      };

      const chartId = analyticsChart ? analyticsChart?.id : editChartId;

      const analyticsChartsPath = isNew
        ? "/analytics_charts"
        : `/analytics_charts/${chartId}`;
      const resp = await useApiCall(analyticsChartsPath, options);

      if (!resp.data?._rawValue?.data) {
        throw new Error("charts.chartsAnalyticsErrorSaving");
      }

      const resultChart = resp.data?._rawValue?.data;
      if (!isNew)
        await this.fetchChartsByTemplateID(String(analyticsChartTemplateID));
      const chartList = this.analyticsCharts.get(
        String(analyticsChartTemplateID),
      );

      if (isNew && resultChart) {
        chartList?.push(resultChart);
      } else if (resultChart) {
        const idx = chartList.findIndex(
          (item: AnalyticsChart) => String(item.id) === String(editChartId),
        );
        chartList[idx] = resultChart;
      }

      return resultChart;
    },

    async removeAnalyticsChart(analyticsChartId: string) {
      const options: RequestOptions = {
        method: "DELETE",
      };

      const resp = await useApiCall(
        `/analytics_charts/${analyticsChartId}`,
        options,
      );
      return resp.status._rawValue;
    },
    async fetchChartsByTemplateID(
      analyticsChartTemplateID: string | null = null,
    ): Promise<void> {
      const selectedChartTemplateID =
        analyticsChartTemplateID ?? this.selectedChartTemplate.id;

      const options: RequestOptions = {
        method: "GET",
      };

      const resp = await useApiCall(
        `/analytics_charts/${selectedChartTemplateID}/charts_by_template_id`,
        options,
      );

      this.analyticsCharts.set(
        selectedChartTemplateID,
        resp.data._rawValue.data,
      );
    },
  },
});
